import React from 'react'

const HIGHLIGHT_STYLE: React.CSSProperties = {
  backgroundColor: '#ffd666',
  padding: '0 1px',
  borderRadius: 2
}

/**
 * Highlights all occurrences of search text within a plain string (case-insensitive).
 * Use for text fields like product name or vendor code.
 */
export const highlightText = (
  text: string,
  search: string
): React.ReactNode => {
  if (!search || !text) return text
  const escaped = search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const parts = text.split(new RegExp(`(${escaped})`, 'gi'))
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <mark key={i} style={HIGHLIGHT_STYLE}>{part}</mark>
        ) : (
          part
        )
      )}
    </>
  )
}

/**
 * Highlights the first occurrence of search digits within a formatted string.
 * Matches by digit sequence only — ignores formatting characters (spaces, dashes,
 * commas, currency symbols) in both the text and the search term.
 *
 * Example: highlightSearchText('+7 (987) 650-12-40', '01240')
 *   → '+7 (987) 65<mark>0-12-40</mark>'
 */
export const highlightSearchText = (
  rawText: string | number,
  search: string
): React.ReactNode => {
  const text = String(rawText ?? '')
  if (!search || !text) return text

  const searchDigits = search.replace(/\D/g, '')
  if (!searchDigits) return text

  const textDigits = text.replace(/\D/g, '')
  const matchStart = textDigits.indexOf(searchDigits)
  if (matchStart === -1) return text

  const matchEnd = matchStart + searchDigits.length - 1

  let digitIdx = 0
  let fmtStart = -1
  let fmtEnd = -1

  for (let i = 0; i < text.length; i++) {
    if (/\d/.test(text[i])) {
      if (digitIdx === matchStart) fmtStart = i
      if (digitIdx === matchEnd) {
        fmtEnd = i
        break
      }
      digitIdx++
    }
  }

  if (fmtStart === -1 || fmtEnd === -1) return text

  return (
    <>
      {text.substring(0, fmtStart)}
      <mark style={HIGHLIGHT_STYLE}>
        {text.substring(fmtStart, fmtEnd + 1)}
      </mark>
      {text.substring(fmtEnd + 1)}
    </>
  )
}
